/*
    This file uses code from @react-three/drei.
    Original Copyright (c) 2020 react-spring
    Source: https://github.com/pmndrs/drei
    License: MIT (see Frontend/src/THIRD-PARTY-LICENSES/drei-MIT.txt)
*/

import { Html, Line } from '@react-three/drei';
import { type ThreeEvent, useThree } from '@react-three/fiber';
import { type FC, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { DoubleSide, type Group, Matrix4, Plane, Ray, Vector3 } from 'three';

import { context } from 'app/modules/wallforming/components/scenes/pivot-controls/context';

const decomposeIntoBasis = (e1: Vector3, e2: Vector3, offset: Vector3) => {
    const i1 =
        Math.abs(e1.x) >= Math.abs(e1.y) && Math.abs(e1.x) >= Math.abs(e1.z)
            ? 0
            : Math.abs(e1.y) >= Math.abs(e1.x) && Math.abs(e1.y) >= Math.abs(e1.z)
              ? 1
              : 2;
    const e2DegrowthOrder = [0, 1, 2].sort((a, b) => Math.abs(e2.getComponent(b)) - Math.abs(e2.getComponent(a)));
    const i2 = i1 === e2DegrowthOrder[0] ? e2DegrowthOrder[1] : e2DegrowthOrder[0];
    const a1 = e1.getComponent(i1);
    const a2 = e1.getComponent(i2);
    const b1 = e2.getComponent(i1);
    const b2 = e2.getComponent(i2);
    const c1 = offset.getComponent(i1);
    const c2 = offset.getComponent(i2);

    const y = (c2 - c1 * (a2 / a1)) / (b2 - b1 * (a2 / a1));
    const x = (c1 - y * b1) / a1;

    return [x, y];
};

const ray = /* @__PURE__ */ new Ray();
const intersection = /* @__PURE__ */ new Vector3();
const offsetMatrix = /* @__PURE__ */ new Matrix4();

export const PlaneSlider: FC<{ dir1: Vector3; dir2: Vector3; axis: 0 | 1 | 2 }> = ({
    dir1,
    dir2,
    axis
}) => {
    const {
        translation,
        translationLimits,
        annotations,
        annotationsClass,
        depthTest,
        scale,
        lineWidth,
        fixed,
        axisColors,
        hoveredColor,
        selectedColor,
        renderOrder,
        onDragStart,
        onDrag,
        onDragEnd,
        userData,
        selectedControl,
        setSelectedControl
    } = useContext(context);

    const gl = useThree((state) => state.gl);
    const camControls = useThree((state) => state.controls) as unknown as { enabled: boolean } | undefined;
    const camControlsRef = useRef(camControls);
    const divRef = useRef<HTMLDivElement>(null!);
    const objRef = useRef<Group>(null!);
    const clickInfo = useRef<{
        clickPoint: Vector3;
        e1: Vector3;
        e2: Vector3;
        plane: Plane;
    } | null>(null);
    const offsetX0 = useRef<number>(0);
    const offsetY0 = useRef<number>(0);
    const [isHovered, setIsHovered] = useState(false);

    const isSelected = selectedControl?.component === 'Rotator' && selectedControl?.axis === axis;
    const color = isHovered
        ? (hoveredColor ?? axisColors[axis])
        : selectedControl
          ? isSelected
              ? (selectedColor ?? axisColors[axis])
              : '#696969'
          : axisColors[axis];

    const opacity = isSelected || isHovered ? 1 : 0.6;

    const onPointerDown = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            setSelectedControl({ component: 'Slider', axis });

            if (annotations) {
                divRef.current.innerText = `${translation.current[(axis + 1) % 3].toFixed(2)}, ${translation.current[
                    (axis + 2) % 3
                ].toFixed(2)}`;
                divRef.current.style.display = 'block';
            }
            e.stopPropagation();
            const clickPoint = e.point.clone();
            const origin = new Vector3().setFromMatrixPosition(objRef.current.matrixWorld);
            const e1 = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 0).normalize();
            const e2 = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 1).normalize();
            const normal = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 2).normalize();
            const plane = new Plane().setFromNormalAndCoplanarPoint(normal, origin);
            clickInfo.current = { clickPoint, e1, e2, plane };
            offsetX0.current = translation.current[(axis + 1) % 3];
            offsetY0.current = translation.current[(axis + 2) % 3];
            onDragStart({ component: 'Slider', axis, origin, directions: [e1, e2, normal] });
            camControls && camControlsRef.current && (camControlsRef.current.enabled = false);

            // @ts-expect-error - releasePointerCapture & PointerEvent#pointerId is not in the type definition
            e.target.setPointerCapture(e.pointerId);
        },
        [setSelectedControl, axis, annotations, translation, onDragStart, camControls]
    );

    const onPointerMove = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            gl.domElement.style.setProperty('cursor', 'pointer');
            if (!isHovered) {
                setIsHovered(true);
            }

            if (clickInfo.current) {
                const { clickPoint, e1, e2, plane } = clickInfo.current;
                const [minX, maxX] = translationLimits?.[(axis + 1) % 3] || [undefined, undefined];
                const [minY, maxY] = translationLimits?.[(axis + 2) % 3] || [undefined, undefined];

                ray.copy(e.ray);
                ray.intersectPlane(plane, intersection);
                ray.direction.negate();
                ray.intersectPlane(plane, intersection);
                intersection.sub(clickPoint);
                let [offsetX, offsetY] = decomposeIntoBasis(e1, e2, intersection);
                /* let offsetY = (intersection.y - (intersection.x * e1.y) / e1.x) / (e2.y - (e2.x * e1.y) / e1.x)
                let offsetX = (intersection.x - offsetY * e2.x) / e1.x */
                if (minX !== undefined) {
                    offsetX = Math.max(offsetX, minX - offsetX0.current);
                }
                if (maxX !== undefined) {
                    offsetX = Math.min(offsetX, maxX - offsetX0.current);
                }
                if (minY !== undefined) {
                    offsetY = Math.max(offsetY, minY - offsetY0.current);
                }
                if (maxY !== undefined) {
                    offsetY = Math.min(offsetY, maxY - offsetY0.current);
                }

                // eslint-disable-next-line react-hooks/immutability
                translation.current[(axis + 1) % 3] = offsetX0.current + offsetX;
                translation.current[(axis + 2) % 3] = offsetY0.current + offsetY;

                if (annotations) {
                    divRef.current.innerText = `${translation.current[(axis + 1) % 3].toFixed(2)}, ${translation.current[
                        (axis + 2) % 3
                    ].toFixed(2)}`;
                }
                offsetMatrix.makeTranslation(
                    offsetX * e1.x + offsetY * e2.x,
                    offsetX * e1.y + offsetY * e2.y,
                    offsetX * e1.z + offsetY * e2.z
                );
                onDrag(offsetMatrix);
            }
        },
        [gl.domElement.style, isHovered, translationLimits, axis, translation, annotations, onDrag]
    );

    const onPointerUp = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            if (annotations) {
                divRef.current.style.display = 'none';
            }
            e.stopPropagation();
            clickInfo.current = null;
            onDragEnd();
            camControls && camControlsRef.current && (camControlsRef.current.enabled = true);

            // @ts-expect-error - releasePointerCapture & PointerEvent#pointerId is not in the type definition
            e.target.releasePointerCapture(e.pointerId);
        },
        [annotations, camControls, onDragEnd]
    );

    const onPointerOut = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            setIsHovered(false);
            gl.domElement.style.setProperty('cursor', 'auto');
        },
        [gl.domElement.style]
    );

    const matrixL = useMemo(() => {
        const dir1N = dir1.clone().normalize();
        const dir2N = dir2.clone().normalize();
        return new Matrix4().makeBasis(dir1N, dir2N, dir1N.clone().cross(dir2N));
    }, [dir1, dir2]);

    const pos1 = fixed ? 1 / 7 : scale / 7;
    const length = fixed ? 0.225 : scale * 0.225;

    const points = useMemo(
        () => [
            new Vector3(0, 0, 0),
            new Vector3(0, length, 0),
            new Vector3(length, length, 0),
            new Vector3(length, 0, 0),
            new Vector3(0, 0, 0)
        ],
        [length]
    );

    return (
        <group ref={objRef} matrix={matrixL} matrixAutoUpdate={false}>
            {annotations && (
                <Html position={[0, 0, 0]}>
                    <div
                        ref={divRef}
                        className={annotationsClass}
                        style={{
                            display: 'none',
                            background: '#151520',
                            color: 'white',
                            padding: '6px 8px',
                            borderRadius: 7,
                            whiteSpace: 'nowrap'
                        }}
                    />
                </Html>
            )}
            <group position={[pos1 * 1.7, pos1 * 1.7, 0]}>
                <mesh
                    renderOrder={renderOrder}
                    scale={length}
                    userData={userData}
                    visible={true}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerOut={onPointerOut}
                    onPointerUp={onPointerUp}
                >
                    <planeGeometry />
                    <meshBasicMaterial
                        color={color}
                        depthTest={depthTest}
                        fog={false}
                        polygonOffsetFactor={-10}
                        side={DoubleSide}
                        polygonOffset
                        transparent
                    />
                </mesh>
                <Line
                    color={color}
                    depthTest={depthTest}
                    fog={false}
                    lineWidth={lineWidth}
                    opacity={opacity}
                    points={points}
                    polygonOffsetFactor={-10}
                    position={[-length / 2, -length / 2, 0]}
                    renderOrder={renderOrder}
                    userData={userData}
                    polygonOffset
                    transparent
                />
            </group>
        </group>
    );
};
